import { useCallback, useEffect, useRef, useState } from 'react';
import { isRequestCanceled } from '@srm/core/http';
import { WinnerSelectionService } from '@services/WinnerSelectionService';
import { ITableKO } from '@models/winnerSelection/ITableKO';

interface IUseTableKOData {
    data: ITableKO | undefined;
    isLoading: boolean;
    errorText: string | undefined;
    reload: () => void;
}

/**
 * Загружает таблицу КО (комиссии отбора) по docId+tableKey.
 * Аналог useTableDemandData (без onDataLoaded — KO в TablePositions не используется).
 *
 * AbortController защищает от stale-ответа при reload() (вызывается после save
 * в OfferCustomForm-модалке) — если пользователь успел переключить documents
 * до прихода ответа, старый response не запишется в state.
 */
export function useTableKOData(docId: string, tableKey: string | undefined): IUseTableKOData {
    const [data, setData] = useState<ITableKO>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [errorText, setErrorText] = useState<string>();
    const controllerRef = useRef<AbortController | null>(null);

    const fetchFor = useCallback(
        (signal: AbortSignal) => {
            if (!tableKey) {
                console.error('useTableKOData. tableKey пустой');
                return;
            }
            setIsLoading(true);
            WinnerSelectionService.fetchTableKOData(docId, tableKey, signal)
                .then((response) => {
                    if (signal.aborted) return;
                    setData(response.data);
                })
                .catch((reason) => {
                    if (isRequestCanceled(reason, signal)) return;
                    setErrorText(reason);
                })
                .finally(() => {
                    if (!signal.aborted) setIsLoading(false);
                });
        },
        [docId, tableKey],
    );

    const reload = useCallback(() => {
        controllerRef.current?.abort();
        const controller = new AbortController();
        controllerRef.current = controller;
        fetchFor(controller.signal);
    }, [fetchFor]);

    useEffect(() => {
        const controller = new AbortController();
        controllerRef.current = controller;
        fetchFor(controller.signal);
        return () => controller.abort();
    }, [fetchFor]);

    return { data, isLoading, errorText, reload };
}
